import type { Collaboration } from '@/redux/collaborationsApi'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Link } from 'react-router-dom'

interface CampaignDetailsTableProps {
  campaigns: Collaboration[]
}

const CampaignDetailsTable = ({ campaigns }: CampaignDetailsTableProps) => {
  if (campaigns.length === 0) {
    return (
      <div className="glass-card backdrop-blur-xs rounded-xl p-8 text-center mt-6 border border-white/5">
        <h2 className="text-xl font-semibold mb-2 text-white">No Performance Data</h2>
        <p className="text-gray-400">
          You don't have any active or completed collaborations to display metrics for.
        </p>
      </div>
    )
  }

  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold mb-4 text-white">Collaboration Details</h2>

      {/* Desktop view - Table */}
      <div className="hidden md:block glass-card backdrop-blur-xs rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Collaboration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Engagement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Completion
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-white/5">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 shrink-0 mr-3">
                        <img
                          src={campaign.brand.logo}
                          alt={campaign.brand.name}
                          className="h-10 w-10 rounded-full"
                        />
                      </div>
                      <div>
                        <Link
                          to={`/collaborations/${campaign.id}`}
                          className="text-sm font-medium text-white hover:text-blue-400 transition-colors"
                        >
                          {campaign.title}
                        </Link>
                        <div className="text-sm text-gray-400">{campaign.brand.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`status-badge ${campaign.status === 'live' ? 'status-live' : 'status-completed'}`}
                    >
                      {campaign.status === 'live' ? 'Live' : 'Completed'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {campaign.platform}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {campaign.metrics?.views.toLocaleString() || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {campaign.metrics?.engagement || 0}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {campaign.metrics?.completionRate || 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile view - Cards - Updated to use glass-card effect like desktop */}
      <div className="md:hidden space-y-4">
        {campaigns.map((campaign) => (
          <Card
            key={campaign.id}
            className="glass-card backdrop-blur-xs rounded-3xl rounded-bl-none shadow-md border border-white/10 overflow-hidden"
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3 mb-1">
                <div className="h-10 w-10 rounded-full overflow-hidden border border-white/10">
                  <img
                    src={campaign.brand.logo}
                    alt={campaign.brand.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <Link
                    to={`/collaborations/${campaign.id}`}
                    className="text-base font-medium text-white hover:text-blue-400 transition-colors"
                  >
                    {campaign.title}
                  </Link>
                  <p className="text-sm text-gray-400">{campaign.brand.name}</p>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span
                  className={`text-xs inline-flex px-2 py-1 rounded-full ${
                    campaign.status === 'live' ? 'status-live' : 'status-completed'
                  }`}
                >
                  {campaign.status === 'live' ? 'Live' : 'Completed'}
                </span>
                <span className="text-xs text-gray-400">{campaign.platform}</span>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-white/5 rounded">
                  <p className="text-xs text-gray-400">Views</p>
                  <p className="font-medium text-white">
                    {campaign.metrics?.views.toLocaleString() || 0}
                  </p>
                </div>
                <div className="p-2 bg-white/5 rounded">
                  <p className="text-xs text-gray-400">Engagement</p>
                  <p className="font-medium text-white">{campaign.metrics?.engagement || 0}%</p>
                </div>
                <div className="p-2 bg-white/5 rounded">
                  <p className="text-xs text-gray-400">Completion</p>
                  <p className="font-medium text-white">
                    {campaign.metrics?.completionRate || 0}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default CampaignDetailsTable
